import { useState, useEffect } from 'react';
//import DashboardLayout from '../dashboard/DashboardLayout';
//import Select from 'react-select';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import AttendanceAnalyticsChart from './AttendanceAnalyticsChart';
import AttendanceClassComparisonChart from './AttendanceClassComparisonChart';
import AttendanceByDayChart from './AttendanceByDayChart';
import TopAttendingStudentsChart from './TopAttendingStudentsChart';



import { saveAs } from 'file-saver';

// Sample data - replace with actual API calls later
const sampleClasses = [
  { id: 'math101', name: 'Math 101', teachers: [
    { name: 'Ms. Smith', avatar: 'https://randomuser.me/api/portraits/women/54.jpg' }
  ] },
  { id: 'eng201', name: 'English 201', teachers: [
    { name: 'Mr. Johnson', avatar: 'https://randomuser.me/api/portraits/men/55.jpg' }
  ] },
  { id: 'sci301', name: 'Science 301', teachers: [
    { name: 'Dr. Lee', avatar: 'https://randomuser.me/api/portraits/men/56.jpg' },
    { name: 'Ms. Carter', avatar: 'https://randomuser.me/api/portraits/women/57.jpg' },
    { name: 'Ms. Kim', avatar: 'https://randomuser.me/api/portraits/women/63.jpg' },
    { name: 'Ms. Lee', avatar: 'https://randomuser.me/api/portraits/women/64.jpg' },
    { name: 'Ms. Martinez', avatar: 'https://randomuser.me/api/portraits/women/65.jpg' }
  ] },
];

// sampleAttendanceRecords removed (not used in current UI)

const dateRangeOptions = [
  { 
    value: 'custom', 
    label: 'Custom Range',
    getRange: () => ({ start: '', end: '' })
  },
  { 
    value: 'today', 
    label: 'Today',
    getRange: () => {
      const today = new Date();
      return {
        start: format(today, 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd')
      };
    }
  },
  { 
    value: 'thisWeek', 
    label: 'This Week',
    getRange: () => {
      const today = new Date();
      return {
        start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    }
  },
  { 
    value: 'thisMonth', 
    label: 'This Month',
    getRange: () => {
      const today = new Date();
      return {
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(endOfMonth(today), 'yyyy-MM-dd')
      };
    }
  },
  { 
    value: 'thisYear', 
    label: 'This Year',
    getRange: () => {
      const today = new Date();
      return {
        start: format(startOfYear(today), 'yyyy-MM-dd'),
        end: format(endOfYear(today), 'yyyy-MM-dd')
      };
    }
  },
  { 
    value: 'allTime', 
    label: 'All Time',
    getRange: () => ({
      start: '2000-01-01', // A reasonable start date
      end: format(new Date(), 'yyyy-MM-dd')
    })
  }
];

// Simple Modal component
function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 p-6 min-w-[350px] max-w-full z-10">
        {children}
      </div>
    </div>
  );
}

// Tooltip component for extra teachers
function TeacherListTooltip({ teachers }) {
  const [show, setShow] = useState(false);
  const extra = teachers.length - 3;
  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="inline-block bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-1 rounded-full cursor-pointer select-none">
        +{extra} more
      </span>
      {show && (
        <div className="absolute left-1/2 z-20 -translate-x-1/2 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[180px] max-w-xs">
          <div className="flex flex-col gap-2">
            {teachers.map((teacher, idx) => (
              <div key={teacher.name + idx} className="flex items-center gap-2">
                <img src={teacher.avatar} alt={teacher.name} className="w-6 h-6 rounded-full object-cover" />
                <span className="text-slate-700 text-xs font-medium">{teacher.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function exportToCSV(students) {
  const headers = ['Student', 'Class', 'Date', 'Teacher(s)', 'Status'];
  const rows = students.map(student => [
    student.name,
    student.className,
    student.date,
    student.teachers.map(t => t.name).join(', '),
    student.status.charAt(0).toUpperCase() + student.status.slice(1)
  ]);
  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, 'attendance_export.csv');
}

export default function ViewAttendancePage() {

  const [attendanceRecords] = useState([]);
  const [loadingRecords] = useState(false);
  const [errorRecords] = useState(null);


  const [selectedClass] = useState('');
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedDateRange] = useState(dateRangeOptions[4]); // Default to last month
  const [search] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;
  const [showCustomRange, setShowCustomRange] = useState(false);



  // Update date range when a predefined option is selected
  useEffect(() => {
    if (selectedDateRange.value !== 'custom') {
      setDateRange(selectedDateRange.getRange());
      setShowCustomRange(false);
    } else {
      setShowCustomRange(true);
    }
  }, [selectedDateRange]);


  const fakeStudentName = (id) => `Student #${id}`;
  const fakeAvatar = (id) => `https://randomuser.me/api/portraits/${id % 2 === 0 ? 'men' : 'women'}/${id}.jpg`;

  // Filter records based on selected class, date range, and search
  const filteredRows = attendanceRecords.filter(r => {
    const matchClass = !selectedClass || String(r.classId) === String(selectedClass);
    const recDate = r.date?.slice(0, 10); // yyyy-mm-dd
    const matchDate = recDate >= dateRange.start && recDate <= dateRange.end;
    return matchClass && matchDate;
  });

  // Flatten records for pagination
  const allStudents = filteredRows.map(r => {
    const cls = sampleClasses.find(c => c.id === String(r.classId));
    return {
      id: r.id,
      name: fakeStudentName(r.studentId),
      avatar: fakeAvatar(r.studentId),
      className: cls?.name || r.classId,
      date: r.date,
      teachers: cls?.teachers || [],
      status: r.status
    };
  });

  // Filter by search (student name)
  const searchedStudents = search
    ? allStudents.filter(student => student.name.toLowerCase().includes(search.toLowerCase()))
    : allStudents;

  // Pagination
  const totalPages = Math.ceil(searchedStudents.length / studentsPerPage);
  const paginatedStudents = searchedStudents.slice(
    (currentPage - 1) * studentsPerPage,
    currentPage * studentsPerPage
  );

  // Options UI removed; keep data minimal to avoid unused variable warnings

  // Helper to format the selected range for display
  function getSelectedRangeLabel() {
    if (selectedDateRange.value === 'custom' && dateRange.start && dateRange.end) {
      return `${dateRange.start} to ${dateRange.end}`;
    }
    return selectedDateRange.label;
  }

  // Custom range modal content
  const customRangeModal = (
    <Modal open={showCustomRange} onClose={() => setShowCustomRange(false)}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">Select Date Range</h3>
          <button
            onClick={() => setShowCustomRange(false)}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
            <DatePicker
              selected={dateRange.start ? new Date(dateRange.start) : null}
              onChange={date => setDateRange(prev => ({ ...prev, start: format(date, 'yyyy-MM-dd') }))}
              selectsStart
              startDate={dateRange.start ? new Date(dateRange.start) : null}
              endDate={dateRange.end ? new Date(dateRange.end) : null}
              maxDate={dateRange.end ? new Date(dateRange.end) : new Date()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              dateFormat="MMM d, yyyy"
              placeholderText="Select start date"
              popperClassName="z-[9999]"
              popperPlacement="bottom-start"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
            <DatePicker
              selected={dateRange.end ? new Date(dateRange.end) : null}
              onChange={date => setDateRange(prev => ({ ...prev, end: format(date, 'yyyy-MM-dd') }))}
              selectsEnd
              startDate={dateRange.start ? new Date(dateRange.start) : null}
              endDate={dateRange.end ? new Date(dateRange.end) : null}
              minDate={dateRange.start ? new Date(dateRange.start) : null}
              maxDate={new Date()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              dateFormat="MMM d, yyyy"
              placeholderText="Select end date"
              popperClassName="z-[9999]"
              popperPlacement="bottom-end"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 mt-2">
          <button
            onClick={() => {
              setDateRange({ start: '', end: '' });
              setShowCustomRange(false);
            }}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            Clear
          </button>
          <button
            onClick={() => setShowCustomRange(false)}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );

  return (

      <div className="max-w-7xl mx-auto p-4 flex flex-col gap-4">

        {/* Charts (unchanged) */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <AttendanceClassComparisonChart />
          <AttendanceByDayChart />
          <TopAttendingStudentsChart />
        </div>
        <div className="w-full mb-4">
          <AttendanceAnalyticsChart />
        </div>
        
        {/* Filters */}
        <div className="bg-white rounded-xl shadow p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-end w-full">
          {/* Class filter ... unchanged */}
          {/* Date range filter ... unchanged */}
          {/* Search ... unchanged */}
          {/* (paste your existing filter JSX here unchanged) */}
        </div>

        {/* Date badge & Export */}
        <div className="flex items-center justify-between mb-2">
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
            {getSelectedRangeLabel()}
          </span>
          <button
            onClick={() => exportToCSV(searchedStudents)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold shadow hover:bg-blue-700 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Export to CSV
          </button>
        </div>
        {customRangeModal}

        {/* Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {loadingRecords && <div className="p-4 text-slate-500">Loading records…</div>}
          {errorRecords && <div className="p-4 text-red-600">Error: {errorRecords}</div>}

          {!loadingRecords && !errorRecords && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="py-3 px-4 text-left text-sm font-semibold text-slate-700">Student</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-slate-700">Class</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-slate-700">Date</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-slate-700">Teacher(s)</th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-slate-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {paginatedStudents.map(student => (
                      <tr key={`${student.id}-${student.date}`} className="hover:bg-slate-50 transition">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <img src={student.avatar} alt={student.name} className="w-8 h-8 rounded-full object-cover" />
                            <span className="font-medium text-slate-700">{student.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{student.className}</td>
                        <td className="py-3 px-4 text-slate-600">{format(new Date(student.date), 'MMM d, yyyy')}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 flex-wrap relative">
                            {student.teachers.slice(0, 3).map((t, idx) => (
                              <img
                                key={t.name + idx}
                                src={t.avatar}
                                alt={t.name}
                                className="w-7 h-7 rounded-full object-cover border-2 border-white shadow-sm"
                                title={t.name}
                              />
                            ))}
                            {student.teachers.length > 3 && (
                              <TeacherListTooltip teachers={student.teachers} />
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
                            ${student.status === 'present' ? 'bg-green-100 text-green-700' :
                              student.status === 'absent' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'}`}>
                            {student.status.charAt(0).toUpperCase() + student.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <div className="text-sm text-slate-700">
                    Showing {((currentPage - 1) * studentsPerPage) + 1} to {Math.min(currentPage * studentsPerPage, searchedStudents.length)} of {searchedStudents.length} records
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => setCurrentPage(i + 1)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium ${
                            currentPage === i + 1
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  
  );
}